import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryClient } from '../../main';
import { invalidarListasDeCobro } from './invalidaciones';
import {
    fetchPlazas,
    fetchActivePlazas,
    fetchPlazaById,
    createPlaza,
    updatePlaza,
    softDeletePlaza,
    deletePlaza,
    verificarDisponibilidadPlazas,
    fetchPlazasByClientId,
    eliminarPlazaYActualizarEventos,
    moverPlaza,
    ProgressResponse,
    crearPlazaConEventos,
    actualizarEstadoEspera,
    fetchDisponibilidadNivel,
    DisponibilidadHorario,
    fetchChoquesPlazas,
    ChoquesResponse
} from '../api/plazaService';
import { Plaza } from '../../types/index';
import { useMemo } from 'react';
import { useTenantCode } from './useTenantCode';

interface NivelDisponibilidad {
    _id: string;
    nombre: string;
}

interface DisponibilidadPlaza {
    dia: string;
    horaInicio: string;
    nivel_id: string;
    nivel: NivelDisponibilidad;
    ocupadas: number;
    disponibles: number;
    plazasActuales: Plaza[]; // Asumiendo que ya tienes la interfaz Plaza definida
}

interface BusquedaPlazaParams {
    dia: string;
    horaInicio: string;
    nivel_id: string;
}

// Query hook para obtener todas las plazas
export const usePlazasQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['plazas', tenantCode],
        queryFn: fetchPlazas
    });
};

// Query hook para obtener plazas activas
export const useActivePlazasQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['plazas', 'active', tenantCode],
        queryFn: fetchActivePlazas
    });
};

// Query hook para obtener plaza por ID
export const usePlazaQuery = (id: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['plaza', id, tenantCode],
        queryFn: () => fetchPlazaById(id),
        enabled: !!id
    });
};

// Mutation para crear plaza
export const useCreatePlazaMutation = () => useMutation({
    mutationFn: createPlaza,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        toast.success('Plaza creada exitosamente');
    },
    onError: () => {
        toast.error('Error al crear la plaza');
    }
});

// Mutation para actualizar plaza
export const useUpdatePlazaMutation = () => useMutation({
    mutationFn: updatePlaza,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        toast.success('Plaza actualizada exitosamente');
    },
    onError: () => {
        toast.error('Error al actualizar la plaza');
    }
});

// Mutation para desactivar plaza
export const useSoftDeletePlazaMutation = () => useMutation({
    mutationFn: ({ id, updatedBy }: { id: string; updatedBy: string }) => 
        softDeletePlaza(id, updatedBy),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        toast.success('Plaza desactivada exitosamente');
    },
    onError: () => {
        toast.error('Error al desactivar la plaza');
    }
});

// Mutation para eliminar plaza permanentemente
export const useDeletePlazaMutation = () => useMutation({
    mutationFn: deletePlaza,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        toast.success('Plaza eliminada permanentemente');
    },
    onError: () => {
        toast.error('Error al eliminar la plaza');
    }
});


export const useVerificarDisponibilidadPlazas = () => {
    return useMutation<DisponibilidadPlaza[], Error, BusquedaPlazaParams[]>({
        mutationFn: verificarDisponibilidadPlazas,
        onSuccess: (data) => {
            const hayPlazasCompletas = data.some(plaza => plaza.disponibles <= 0);
            if (hayPlazasCompletas) {
                toast.warning('Algunas plazas están completas');
            } else {
                toast.success('Hay plazas disponibles');
            }
        },
        onError: () => {
            toast.error('Error al verificar disponibilidad');
        }
    });
};

/**
 * Clases ya generadas en las que el cliente NO cabría con estas plazas (llenas
 * por recuperaciones, clases de prueba…). Sin caché, y se refresca cada 30 s y
 * al volver a la pestaña: mientras se rellena el formulario otros clientes
 * siguen reservando, y el aviso tiene que ir al día.
 */
export const useChoquesPlazasQuery = (
    plazas: BusquedaPlazaParams[],
    opts: { clienteId?: string; prospectId?: string; enabled?: boolean } = {}
) => {
    const tenantCode = useTenantCode();
    const { clienteId, prospectId, enabled = true } = opts;
    return useQuery<ChoquesResponse>({
        queryKey: ['plazas', 'choques', plazas, clienteId, prospectId, tenantCode],
        queryFn: () => fetchChoquesPlazas({ plazas, clienteId, prospectId }),
        enabled: enabled && plazas.length > 0,
        staleTime: 0,
        gcTime: 0,
        refetchInterval: 30_000,
        refetchOnWindowFocus: true,
    });
};

/** Error del backend al guardar cuando se llenó alguna clase desde el aviso (409). */
export const esChoquesCambiados = (error: unknown) =>
    (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'CHOQUES_CAMBIADOS';

// Query hook para obtener plazas por cliente_id
export const usePlazasByClientIdQuery = (cliente_id: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['plazas', 'cliente', cliente_id, tenantCode],
        queryFn: () => fetchPlazasByClientId(cliente_id),
        enabled: !!cliente_id
    });
};


// Hook para eliminar plaza y verificar progreso
export const useEliminarPlazaMutation = () => {
    const iniciarProceso = useMutation({
        mutationFn: eliminarPlazaYActualizarEventos,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['plazas'] });
            queryClient.invalidateQueries({ queryKey: ['client'] });
            // El back recalcula el importe al quitar la plaza.
            invalidarListasDeCobro();
            // Mostrar feedback más detallado
            toast.success(
                `Plaza desactivada exitosamente. ${data.eventosActualizados} eventos actualizados.`, 
                { autoClose: 4000 }
            );
        },
        onError: () => {
            toast.error('Error al eliminar la plaza');
        }
    });
    
    return iniciarProceso;
};

// Hook para mover/editar la plaza de un cliente a un nuevo slot (M5)
export const useMoverPlazaMutation = () => useMutation({
    mutationFn: moverPlaza,
    onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ['plazas'] });
        queryClient.invalidateQueries({ queryKey: ['client'] });
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        // Mover de nivel puede cambiar de tarifa y de importe.
        invalidarListasDeCobro();
        toast.success(
            `Plaza movida. ${data.eventosRemovidos} eventos liberados, ${data.eventosAgregados} nuevos asignados.`,
            { autoClose: 4000 }
        );
    },
    onError: (error: unknown) => {
        const message =
            (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Error al mover la plaza';
        toast.error(message);
    },
});

// Hook para eliminar plaza y verificar progreso
export const useCrearPlazasEventosMutation = () => {
    const iniciarProceso = useMutation({
        mutationFn: crearPlazaConEventos,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['plazas'] });
            queryClient.invalidateQueries({ queryKey: ['client'] });
            // El back recalcula el importe al añadir la plaza.
            invalidarListasDeCobro();
             // Mostrar feedback más detallado
            toast.success(
                `Plaza creada exitosamente. ${data.eventosActualizados} eventos actualizados.`, 
                { autoClose: 4000 }
            );
        },
        onError: (error: any) => {
            // Se llenó alguna clase desde el aviso: no es un error, lo cuenta el aviso al refrescarse.
            if (esChoquesCambiados(error)) return;
            toast.error(error?.response?.data?.message || 'Error al crear la plaza');
        }
    });

    return iniciarProceso;
};


// Hook para actualizar el estado de espera de un cliente y sus plazas
export const useActualizarEstadoEsperaMutation = () => {
    return useMutation({
        mutationFn: actualizarEstadoEspera,
        onSuccess: (data) => {
            // Invalidar consultas relacionadas
            queryClient.invalidateQueries({ queryKey: ['client', data.cliente._id] });
            queryClient.invalidateQueries({ queryKey: ['plazas', 'cliente', data.cliente._id] });
            queryClient.invalidateQueries({ queryKey: ['plazas'] });
            // P4 — pausar y reactivar entra y saca al cliente de clases reales, así
            // que el calendario y los eventos cacheados quedan obsoletos.
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            queryClient.invalidateQueries({ queryKey: ['client-recoveries', data.cliente._id] });

             // Mostrar mensaje de éxito personalizado según la acción realizada
            const accion = data.cliente.enEspera ? 'puesto en espera' : 'reactivado';
            toast.success(
                `Cliente ${accion} correctamente. ${data.plazasActualizadas} plazas actualizadas.`,
                { autoClose: 4000 }
            );

            // P4 — lo que ha pasado con las plazas de verdad, que es lo que el
            // operador tiene que poder contarle al cliente.
            if (data.cliente.enEspera && data.plazasLiberadas) {
                toast.info(
                    data.plazasLiberadas === 1
                        ? 'Se ha liberado su sitio en 1 clase del periodo.'
                        : `Se ha liberado su sitio en ${data.plazasLiberadas} clases del periodo.`,
                    { autoClose: 6000 }
                );
            }
            if (!data.cliente.enEspera && data.reactivacion) {
                const { eventosActualizados, clasesCompletas } = data.reactivacion;
                toast.info(
                    `Ha vuelto a entrar en ${eventosActualizados} clase(s).`
                    + (clasesCompletas.length
                        ? ` ${clasesCompletas.length} estaban completas y se le ha compensado.`
                        : ''),
                    { autoClose: 8000 }
                );
            }
        },
        onError: (error: any) => {
            // Manejo detallado de errores
            const errorMsg = error?.response?.data?.message || 'Error al actualizar estado de espera';
            toast.error(errorMsg, { autoClose: false });
            console.error('Error al actualizar estado de espera:', error);
        }
    });
};

// ============================================
// NUEVO: Hook para disponibilidad de horarios por nivel
// ============================================

/**
 * Helper para calcular el rango de fechas de búsqueda de disponibilidad:
 * ventana rodante de los próximos 30 días desde hoy. (Se retiró el antiguo
 * salto a septiembre durante agosto, que ocultaba los horarios reales.)
 */
export const calcularRangoFechas = () => {
    const fechaInicio = new Date();
    const fechaFin = new Date();
    fechaFin.setDate(fechaFin.getDate() + 30); // 30 días hacia adelante

    return {
        startDate: fechaInicio.toISOString(),
        endDate: fechaFin.toISOString(),
        showAgostoMessage: false
    };
};

/**
 * Hook para obtener disponibilidad de horarios de un nivel
 * Incluye cache de 30 segundos y manejo automático de lógica de agosto
 */
export const useDisponibilidadNivelQuery = (nivelId: string | null) => {
    // Calcular fechas con lógica de agosto
    const { startDate, endDate, showAgostoMessage } = useMemo(() => 
        calcularRangoFechas(), 
        []
    );
    const tenantCode = useTenantCode();
    
    const query = useQuery<DisponibilidadHorario[]>({
        queryKey: ['disponibilidad-nivel', nivelId, startDate, endDate, tenantCode],
        queryFn: async () => {
            const response = await fetchDisponibilidadNivel(nivelId!, startDate, endDate);
            return response.horarios;
        },
        enabled: !!nivelId,
        staleTime: 30000,        // 30 segundos
        gcTime: 5 * 60 * 1000, // 5 minutos (antes cacheTime en v4)
        refetchOnWindowFocus: false,
    });
    
    return {
        ...query,
        showAgostoMessage
    };
};

/**
 * Hook para obtener disponibilidad de horarios por día y nivel específico
 * Usado para frecuencias 2x y 3x semana con niveles diferentes
 */
export const useDisponibilidadDiaNivelQuery = (dia: string | null, nivelId: string | null) => {
    // Calcular fechas con lógica de agosto
    const { startDate, endDate, showAgostoMessage } = useMemo(() => 
        calcularRangoFechas(), 
        []
    );
    const tenantCode = useTenantCode();
    
    const query = useQuery<DisponibilidadHorario[]>({
        queryKey: ['disponibilidad-dia-nivel', dia, nivelId, startDate, endDate, tenantCode],
        queryFn: async () => {
            const response = await fetchDisponibilidadNivel(nivelId!, startDate, endDate);
            // Filtrar solo los horarios del día específico
            return response.horarios.filter((h: any) => h.dia === dia);
        },
        enabled: !!nivelId && !!dia,
        staleTime: 30000,        // 30 segundos
        gcTime: 5 * 60 * 1000, // 5 minutos
        refetchOnWindowFocus: false,
    });
    
    return {
        ...query,
        showAgostoMessage
    };
};